import user from '@/api/modules/user'
import apiRiskSurvey from '@/api/modules/risk-survey'
import { AppHolder } from '@/utils/app-holder'
import { featureFlag } from '@/../app-config.js'

const getDefaultState = () => {
  return {
    email: null,
    profile: null,
    questions: null,
    plans: [],
    invitation: null,
    paymentSources: [],
    invoices: [],
    aggregationHistory: []
  }
}

const state = getDefaultState()

const actions = {
  fetch (context, cached = true) {
    const { profile } = context.state
    if (cached && profile) {
      return Promise.resolve(profile)
    }

    return user.fetch()
      .then(data => {
        user.fetchProfile()
          .then(profile => {
            context.commit('fetch', Object.assign(data, profile.profile))
          })
          .catch(() => {
            context.commit('fetch', data)
          })
      })
  },

  fetchConfirm (context) {
    return user.fetch()
      .then(data => context.commit('fetch', data))
  },

  fetchProfile (context) {
    user.fetchProfile()
      .then(profile => {
        context.commit('fetch', profile.profile)
      })
      .catch((res) => {
        return Promise.reject(res)
      })
  },

  fetchQuestions (context, cached = true) {
    const { questions } = context.state
    if (cached && questions) {
      return Promise.resolve(questions)
    }

    return apiRiskSurvey.fetchQuestions()
      .then(({ riskSurvey }) => context.commit('fetchQuestions', riskSurvey.filter(({type}) => type === 'profile')))
  },

  getToken (context) {
    return user.getToken()
      .then(token => token.accessToken)
  },

  register (context, credentials) {
    return user.create(credentials)
      .then(data => context.commit('register', data))
  },

  update (context, fields) {
    return user.update(fields)
      .then(data => context.commit('update', data))
  },

  resendConfirmation () {
    return user.resendConfirmation()
  },

  validateCgu (context, fields) {
    return user.validateCgu(fields)
      .then(data => {
        return Promise.resolve(data.nextStep)
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  deleteProfile () {
    return user.deleteProfile()
  },

  confirmDeleteProfile (context, token) {
    return user.confirmDeleteProfile(token)
      .then(() => context.dispatch('unauthenticate'))
      .then(AppHolder.store.dispatch('reset'))
  },

  fetchPlans (context) {
    return user.fetchPlans()
      .then(({ plans }) => context.commit('fetchPlans', plans))
  },

  verifyCoupon (context, { coupon, planId }) {
    return user.verifyCoupon(coupon, planId)
  },

  subscribe (context, data) {
    return user.subscribe(data)
  },

  fetchInvitation (context, { invitationToken, cache = true }) {
    if (cache && context.state.invitation && context.state.invitation.token === invitationToken) {
      return Promise.resolve()
    }
    return user.fetchInvitation(invitationToken)
      .then(data => context.commit('setInvitation', {
        ...data.invitation,
        token: invitationToken
      }))
  },

  markInvitationAsRead (context, invitationToken) {
    return user.markInvitationAsRead(invitationToken)
  },

  fetchPaymentSources (context, cache = true) {
    if (cache && context.state.paymentSources.length) {
      return Promise.resolve()
    }
    return user.fetchPaymentSources()
      .then(data => context.commit('setPaymentSources', data))
  },

  updatePaymentSource (context, token) {
    return user.updatePaymentSource(token)
      .then((data) => context.commit('setPaymentSources', {
        paymentSources: [data]
      }))
  },

  updateDefaultPaymentInfo (context, { payload }) {
    return user.postDefaultPaymentInfo(payload)
  },

  fetchInvoices (context, cache = true) {
    if (cache && context.state.invoices.length) {
      return Promise.resolve()
    }
    return user.fetchInvoices()
      .then(data => context.commit('setInvoices', data))
  }
}

const mutations = {
  fetch (state, data) {
    state.profile = Object.assign({}, state.profile, data)
  },

  fetchQuestions (state, data) {
    state.questions = data
  },

  register (state, data) {
    state.profile = data
  },

  update (state, data) {
    Object.keys(data).forEach(key => Object.assign(state[key], data[key]))
  },

  fetchPlans (state, data) {
    state.plans = data
  },

  setInvitation (state, invitation) {
    state.invitation = invitation
  },

  setPaymentSources (state, data) {
    state.paymentSources = data.paymentSources
  },

  setInvoices (state, data) {
    state.invoices = data.invoices
  },

  resetState (state) {
    Object.assign(state, getDefaultState())
  },

  // Saves aggregation refreshes to avoid the need for a quick refresh. Moreover, this is not saved in the BI store, which is regularly reset.  
  addAggregationHistory (state, data) {
    state.aggregationHistory = [...state.aggregationHistory.filter(a => a.connexionId !== data.connexionId), data];
  },
}

const getters = {
  userName ({ profile }) {
    if (profile) {
      const {
        firstName,
        lastName,
        email,
        externalId
      } = profile

      if (externalId && featureFlag('aPreferUserId')) {
        return externalId
      }

      return firstName ? `${firstName} ${lastName || ''}` : email
    }

    return ''
  },

  isProfileComplete ({ profile }) {
    if (profile) {
      const {
        'complete?': isComplete,
        recommendedRisk
      } = profile

      return {
        risk: recommendedRisk,
        infos: isComplete,
        full: recommendedRisk && isComplete
      }
    }

    return false
  },

  getFeatures (state, getters, rootState, rootGetters) {
    const isAdvisor = rootGetters['auth/isAdvisor']
    const advisorProfile = rootState.advisor.profile

    // If it's an advisor we override the available features
    if (isAdvisor && advisorProfile) return advisorProfile.features
    
    return state.profile
      ? state.profile.features
      : []
  },

  getCoupon ({ profile }) {
    if (profile) {
      const { coupon } = profile
      return coupon
    }
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
